<script setup>
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { router, useForm, Link } from '@inertiajs/vue3'
import DriverPortalLayout from '@/Layouts/DriverPortalLayout.vue'
import EventHistory from '@/Components/DriverPortal/EventHistory.vue'
import { useDeliveryGeolocation } from '@/composables/useDeliveryGeolocation'

defineOptions({ layout: DriverPortalLayout })
const { t } = useI18n()

const props = defineProps({
  delivery: { type: Object, required: true },
  events:   { type: Array, default: () => [] }
})

const showProofModal = ref(false)
const proofForm = useForm({ proof_photos: [] })
const previews = ref([])

const canComplete = computed(() => proofForm.proof_photos.length > 0)

// Geolocation share — opt-in by tap. Stops automatically when the delivery
// transitions to a terminal state (server rejects with delivery_terminal
// and the composable calls stop()).
const geo = useDeliveryGeolocation({ deliveryId: props.delivery.id })

// Auto-stop when the page transitions to a terminal status (e.g. completion
// happened in another tab / via server push) so we don't keep pinging.
watch(
  () => props.delivery.status,
  (s) => { if (['completed', 'cancelled'].includes(s) && geo.isSharing.value) geo.stop() }
)

function toggleShareLocation() {
  if (geo.isSharing.value) {
    geo.stop()
  } else {
    geo.start()
  }
}

const shareEligible = computed(() => ['assigned', 'in_progress'].includes(props.delivery.status))

const lastFixAgeSec = computed(() => {
  if (!geo.lastSentAt.value) return null
  return Math.round((Date.now() - geo.lastSentAt.value.getTime()) / 1000)
})

function claim() {
  if (!confirm(t('driver_portal.delivery.confirm_claim'))) return
  router.patch(`/deliveries/${props.delivery.id}/claim`)
}

function startTrip() {
  router.patch(`/deliveries/${props.delivery.id}/start`)
}

function openCompleteModal() {
  proofForm.reset()
  previews.value = []
  showProofModal.value = true
}

function closeCompleteModal() {
  showProofModal.value = false
  previews.value.forEach(p => URL.revokeObjectURL(p.url))
  previews.value = []
  proofForm.reset()
}

function onFilesPicked(e) {
  const files = Array.from(e.target.files || []).slice(0, 10)
  proofForm.proof_photos = files
  previews.value.forEach(p => URL.revokeObjectURL(p.url))
  previews.value = files.map(f => ({ url: URL.createObjectURL(f), name: f.name }))
}

function submitComplete() {
  if (!canComplete.value) return
  proofForm.patch(`/deliveries/${props.delivery.id}/complete`, {
    forceFormData: true,
    onSuccess: () => closeCompleteModal()
  })
}

function cancelDelivery() {
  if (!confirm(t('driver_portal.delivery.confirm_cancel'))) return
  router.patch(`/deliveries/${props.delivery.id}/cancel`)
}

function mapsHref(d) {
  if (d.lat && d.lng) return `https://maps.google.com/?q=${d.lat},${d.lng}`
  return `https://maps.google.com/?q=${encodeURIComponent(d.address)}`
}
</script>

<template>
  <Link
    href="/"
    class="back"
  >
    ← {{ t('driver_portal.delivery.back') }}
  </Link>

  <h1 class="title">
    {{ delivery.kind === 'handover' ? '↗' : '↙' }}
    {{ delivery.kind === 'handover' ? t('driver_portal.delivery.kind.handover') : t('driver_portal.delivery.kind.pickup') }}
    <span :class="['pill', `pill--${delivery.status}`]">{{ t(`driver_portal.delivery.status.${delivery.status}`) }}</span>
  </h1>

  <!-- Customer card -->
  <section class="card">
    <h2 class="card__heading">
      {{ t('driver_portal.delivery.customer') }}
    </h2>
    <p class="big">
      {{ delivery.customer_name }}
    </p>
    <a
      v-if="delivery.customer_phone"
      :href="`tel:${delivery.customer_phone}`"
      class="link"
    >
      📞 {{ delivery.customer_phone }}
    </a>
    <a
      :href="mapsHref(delivery)"
      target="_blank"
      rel="noopener"
      class="link"
    >
      📍 {{ delivery.address }}
    </a>
    <p
      v-if="delivery.scheduled_for"
      class="meta"
    >
      {{ delivery.scheduled_for }}
    </p>
  </section>

  <!-- Vehicle card -->
  <section class="card">
    <h2 class="card__heading">
      {{ t('driver_portal.delivery.vehicle') }}
    </h2>
    <img
      v-if="delivery.vehicle.photo_url"
      :src="delivery.vehicle.photo_url"
      class="photo"
      :alt="`${delivery.vehicle.make} ${delivery.vehicle.model}`"
    >
    <p class="big">
      {{ delivery.vehicle.make }} {{ delivery.vehicle.model }}
    </p>
    <p class="meta">
      {{ delivery.vehicle.registration }}
    </p>
  </section>

  <!-- Operator note -->
  <section
    v-if="delivery.notes"
    class="card card--note"
  >
    <h2 class="card__heading">
      {{ t('driver_portal.delivery.operator_note') }}
    </h2>
    <p class="note-text">
      {{ delivery.notes }}
    </p>
  </section>

  <!-- Share location card — opt-in geolocation watch -->
  <section
    v-if="shareEligible"
    :class="['card', 'share-card', geo.isSharing.value && 'share-card--on']"
  >
    <div class="share-card__row">
      <div class="share-card__left">
        <h2 class="card__heading">
          {{ t('driver_portal.delivery.share.heading') }}
        </h2>
        <p class="share-card__hint">
          {{ geo.isSharing.value
            ? t('driver_portal.delivery.share.sharing')
            : t('driver_portal.delivery.share.hint') }}
        </p>
        <p
          v-if="geo.isSharing.value && lastFixAgeSec !== null"
          class="share-card__age"
        >
          {{ t('driver_portal.delivery.share.last_fix', { sec: lastFixAgeSec }) }}
        </p>
        <p
          v-if="geo.error.value"
          class="share-card__err"
        >
          {{ geo.error.value }}
        </p>
      </div>
      <button
        :class="['share-toggle', geo.isSharing.value && 'share-toggle--on']"
        type="button"
        @click="toggleShareLocation"
      >
        {{ geo.isSharing.value
          ? t('driver_portal.delivery.share.stop')
          : t('driver_portal.delivery.share.start') }}
      </button>
    </div>
  </section>

  <!-- Proof photos shown after completion -->
  <section
    v-if="delivery.proof_photos && delivery.proof_photos.length"
    class="card"
  >
    <h2 class="card__heading">
      {{ t('driver_portal.delivery.proof.heading') }}
    </h2>
    <div class="proof-grid">
      <a
        v-for="photo in delivery.proof_photos"
        :key="photo.id"
        :href="photo.url"
        target="_blank"
        rel="noopener"
        class="proof-thumb"
      >
        <img
          :src="photo.url"
          alt=""
        >
      </a>
    </div>
  </section>

  <!-- Action buttons -->
  <div class="actions">
    <button
      v-if="delivery.status === 'pending'"
      class="action action--primary"
      @click="claim"
    >
      {{ t('driver_portal.delivery.claim') }}
    </button>
    <button
      v-if="delivery.status === 'assigned'"
      class="action action--primary"
      @click="startTrip"
    >
      {{ t('driver_portal.delivery.start') }}
    </button>
    <button
      v-if="delivery.status === 'in_progress'"
      class="action action--primary"
      @click="openCompleteModal"
    >
      {{ t('driver_portal.delivery.complete') }}
    </button>
    <button
      v-if="['assigned', 'in_progress'].includes(delivery.status)"
      class="action action--secondary"
      @click="cancelDelivery"
    >
      {{ t('driver_portal.delivery.cancel') }}
    </button>
    <Link
      v-if="['completed', 'cancelled'].includes(delivery.status)"
      href="/"
      class="action action--secondary"
    >
      {{ t('driver_portal.delivery.back_to_dashboard') }}
    </Link>
  </div>

  <EventHistory :events="events" />

  <!-- Proof-photo modal -->
  <div
    v-if="showProofModal"
    class="modal-overlay"
    @click.self="closeCompleteModal"
  >
    <div class="modal-card">
      <h2 class="modal-title">
        {{ t('driver_portal.delivery.proof.title') }}
      </h2>
      <p class="modal-hint">
        {{ t('driver_portal.delivery.proof.hint') }}
      </p>

      <label class="file-picker">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          capture="environment"
          multiple
          @change="onFilesPicked"
        >
        <span class="file-picker__label">
          {{ proofForm.proof_photos.length
            ? t('driver_portal.delivery.proof.picked', { n: proofForm.proof_photos.length })
            : t('driver_portal.delivery.proof.pick_cta') }}
        </span>
      </label>

      <div
        v-if="previews.length"
        class="proof-grid proof-grid--preview"
      >
        <div
          v-for="(p, i) in previews"
          :key="i"
          class="proof-thumb"
        >
          <img
            :src="p.url"
            :alt="p.name"
          >
        </div>
      </div>

      <div class="modal-actions">
        <button
          class="action action--secondary"
          @click="closeCompleteModal"
        >
          {{ t('driver_portal.delivery.proof.cancel') }}
        </button>
        <button
          class="action action--primary"
          :disabled="!canComplete || proofForm.processing"
          @click="submitComplete"
        >
          {{ proofForm.processing
            ? t('driver_portal.delivery.proof.submitting')
            : t('driver_portal.delivery.proof.submit') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.back {
  display: inline-block;
  margin-bottom: 0.75rem;
  color: var(--muted);
  text-decoration: none;
  font-size: 0.875rem;
}
.title {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0 0 1rem;
  color: var(--ink);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.card {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  margin-bottom: 0.75rem;
}
.card__heading {
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  margin: 0 0 0.5rem;
}
.card--note {
  background: color-mix(in oklch, var(--warn) 6%, var(--surface));
  border-color: color-mix(in oklch, var(--warn) 30%, var(--line));
}
.note-text { margin: 0; font-size: 0.9375rem; color: var(--ink); }
.big {
  font-size: 1.1rem;
  font-weight: 500;
  margin: 0 0 0.5rem;
  color: var(--ink);
}
.meta { color: var(--muted); margin: 0.4rem 0 0; font-size: 0.875rem; }
.link {
  display: block;
  padding: 0.55rem 0.75rem;
  background: var(--surfaceAlt);
  border-radius: 0.5rem;
  margin-bottom: 0.4rem;
  color: var(--ink);
  text-decoration: none;
  font-size: 0.9375rem;
}
.photo {
  width: 100%;
  max-height: 12rem;
  object-fit: cover;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  background: var(--surfaceAlt);
}
.proof-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(5rem, 1fr));
  gap: 0.5rem;
}
.proof-grid--preview { margin-top: 0.75rem; }
.proof-thumb {
  display: block;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  border-radius: 0.5rem;
  background: var(--surfaceAlt);
  border: 1px solid var(--line);
}
.proof-thumb img { width: 100%; height: 100%; object-fit: cover; }
.actions { margin: 1.5rem 0; }
.action {
  display: block;
  width: 100%;
  padding: 0.85rem;
  border: 0;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 0.5rem;
  min-height: 2.75rem;
  text-align: center;
  text-decoration: none;
}
.action:disabled { opacity: 0.5; cursor: not-allowed; }
.action--primary  { background: var(--accent); color: var(--accentInk); }
.action--secondary {
  background: transparent;
  color: var(--danger);
  border: 1px solid var(--line);
}
/* Status pills */
.pill {
  display: inline-block;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 0.25rem;
}
.pill--pending     { background: color-mix(in oklch, var(--warn) 14%, var(--surface));   color: var(--warn); }
.pill--assigned    { background: color-mix(in oklch, var(--accent) 14%, var(--surface)); color: var(--accent); }
.pill--in_progress { background: color-mix(in oklch, var(--accent) 22%, var(--surface)); color: var(--accent); }
.pill--completed   { background: color-mix(in oklch, var(--ok) 14%, var(--surface));     color: var(--ok); }
.pill--cancelled   { background: color-mix(in oklch, var(--muted) 14%, var(--surface));  color: var(--muted); }

/* Modal */
.modal-overlay {
  position: fixed; inset: 0; z-index: 50;
  background: rgba(0, 0, 0, 0.6);
  display: flex; align-items: flex-end; justify-content: center;
  padding: 0;
}
@media (min-width: 640px) {
  .modal-overlay { align-items: center; padding: 1rem; }
}
.modal-card {
  background: var(--surface);
  width: 100%;
  max-width: 28rem;
  border-radius: 0.75rem 0.75rem 0 0;
  padding: 1.25rem 1.25rem max(env(safe-area-inset-bottom), 1.25rem);
  max-height: 90vh;
  overflow-y: auto;
}
@media (min-width: 640px) {
  .modal-card { border-radius: 0.75rem; }
}
.modal-title {
  font-size: 1.1rem; font-weight: 600; margin: 0 0 0.5rem; color: var(--ink);
}
.modal-hint {
  margin: 0 0 1rem; color: var(--muted); font-size: 0.875rem;
}
.file-picker {
  display: block; cursor: pointer;
  padding: 1.25rem 0.75rem;
  border: 2px dashed var(--line);
  border-radius: 0.5rem;
  background: var(--surfaceAlt);
  text-align: center;
  color: var(--inkSoft);
  font-weight: 500;
}
.file-picker input { display: none; }
.file-picker__label { display: block; font-size: 0.9375rem; }
.modal-actions {
  display: flex; gap: 0.5rem; margin-top: 1rem;
}
.modal-actions .action { margin-bottom: 0; }

/* Share location card */
.share-card { transition: border-color 0.2s, background 0.2s; }
.share-card--on {
  background: color-mix(in oklch, var(--ok) 8%, var(--surface));
  border-color: color-mix(in oklch, var(--ok) 35%, var(--line));
}
.share-card__row {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem;
}
.share-card__left { flex: 1; min-width: 0; }
.share-card__hint { margin: 0; color: var(--inkSoft); font-size: 0.875rem; }
.share-card__age  { margin: 0.4rem 0 0; color: var(--ok); font-size: 0.8125rem; font-weight: 600; }
.share-card__err  { margin: 0.4rem 0 0; color: var(--danger); font-size: 0.8125rem; }
.share-toggle {
  flex-shrink: 0;
  padding: 0.55rem 0.85rem;
  border: 1px solid var(--line);
  border-radius: var(--radius-button, 8px);
  background: var(--surface);
  color: var(--ink);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
}
.share-toggle--on {
  background: var(--ok);
  color: var(--accentInk);
  border-color: var(--ok);
}
</style>
